import { computed, getCurrentInstance, nextTick, shallowRef, watch, type WritableComputedRef } from "vue";
import type {
    LocationQuery,
    LocationQueryRaw,
    LocationQueryValue,
    RouteLocationNormalizedLoaded,
    RouteRecordNameGeneric,
    RouteRecordNormalized,
    Router
} from "vue-router";

/*
  Rank 31: a list's search, dates, page, tab, view and sort live in the route query, so a filtered view survives a
  refresh, can be bookmarked or shared, and Back steps through what was pushed. Only the query changes, so the
  router's scroll handling (router/main-scroll.ts) leaves the page where it is.

  Keys that change as the member types or pages (q, page, dates) are replaced; a tab, view or sort is pushed, so Back
  undoes it.
*/

export type QueryHistoryMode = 'replace' | 'push';

/** Query keys to set; `undefined` removes the key. */
export type QueryPatch = Record< string, string | readonly string[] | undefined >;

export interface RouteQueryCodec< T > {
    parse( raw: string | undefined ): T;
    /** `undefined` leaves the key out of the URL (the default value). */
    serialize( value: T ): string | undefined;
}

export interface RouteQueryStateOptions {
    history?: QueryHistoryMode;
    /** Route names that are one page for this value: see createRouteOwnership. */
    routeNames?: readonly RouteRecordNameGeneric[];
}

/** The first value of a query key: `?q=a&q=b` reads 'a' and a bare `?q` reads ''. */
export function firstQueryValue( value: LocationQueryValue | LocationQueryValue[] | undefined ): string | undefined {
    const first: LocationQueryValue | undefined = Array.isArray( value ) ? value[ 0 ] : value;
    if ( first === undefined ) return undefined;
    return first ?? '';
}

/** Every non-empty value of a query key, in URL order. */
export function queryValues( value: LocationQueryValue | LocationQueryValue[] | undefined ): string[] {
    const values: LocationQueryValue[] = Array.isArray( value ) ? value : [ value ?? null ];
    return values.filter( ( item: LocationQueryValue ): item is string => !!item );
}

function queryValueKey( value: LocationQueryValue | LocationQueryValue[] | readonly string[] | undefined ): string {
    if ( value === undefined ) return '';
    return JSON.stringify( Array.isArray( value ) ? value : [ value ] );
}

/** True when both queries hold the same keys and values. */
export function sameQuery( left: LocationQuery | LocationQueryRaw, right: LocationQuery | LocationQueryRaw ): boolean {
    const keys = new Set( [ ...Object.keys( left ), ...Object.keys( right ) ] );
    for ( const key of keys ) {
        if ( queryValueKey( left[ key ] as LocationQueryValue ) !== queryValueKey( right[ key ] as LocationQueryValue ) ) return false;
    }
    return true;
}

/*********************************************************************/
//                        BATCHED QUERY WRITES                       //
/*********************************************************************/

interface ScheduledWrite {
    path: string;
    patch: QueryPatch;
    push: boolean;
}

// One navigation per update, whoever asked: a view change that also resets the page lands as one push. Writes wait
// for Vue's pending flush (so the page reset a watcher makes joins them) and for our previous navigation, which a
// second one started meanwhile would cancel, losing a pushed history entry.
const scheduledWrites = new WeakMap< Router, ScheduledWrite >();
const writeChains = new WeakMap< Router, Promise< void > >();
const navigating = new WeakSet< Router >();

interface NavigationTracker {
    // The resolved target of our own navigation in flight
    ownTarget: string | null;
    // A navigation the member started (a link, Back) that is still in its guards
    foreignTarget: string | null;
}

const trackers = new WeakMap< Router, NavigationTracker >();

/**
 * Watches the router for navigations that are not ours. The router keeps only the newest navigation, so a query write
 * started while the member's click is still in its guards (a slow auth guard) would cancel that click; such a write is
 * dropped instead. Called when a view binds to the query, so the hooks are in place before the member navigates.
 */
function trackNavigations( router: Router ): NavigationTracker {
    let tracker: NavigationTracker | undefined = trackers.get( router );
    if ( !tracker ) {
        const created: NavigationTracker = { ownTarget: null, foreignTarget: null };
        router.beforeEach( ( to ) => {
            created.foreignTarget = to.fullPath === created.ownTarget ? null : to.fullPath;
        } );
        router.afterEach( () => { created.foreignTarget = null; } );
        router.onError( () => { created.foreignTarget = null; } );
        trackers.set( router, created );
        tracker = created;
    }
    return tracker;
}

function applyPatch( base: LocationQuery, patch: QueryPatch ): LocationQuery {
    const next: LocationQuery = { ...base };
    for ( const [ key, value ] of Object.entries( patch ) ) {
        if ( value === undefined || ( Array.isArray( value ) && value.length === 0 ) ) {
            delete next[ key ];
        } else {
            next[ key ] = Array.isArray( value ) ? [ ...value ] : value as string;
        }
    }
    return next;
}

async function flushWrite( router: Router, write: ScheduledWrite ): Promise< void > {
    if ( scheduledWrites.get( router ) === write ) {
        scheduledWrites.delete( router );
    }

    const tracker: NavigationTracker = trackNavigations( router );
    const current: RouteLocationNormalizedLoaded = router.currentRoute.value;
    // The member left (or is leaving) the page the write was for; the next page's query is not this list's to change
    if ( current.path !== write.path || tracker.foreignTarget !== null ) return;

    const query: LocationQuery = applyPatch( current.query, write.patch );
    if ( sameQuery( query, current.query ) ) return;

    const location = { path: current.path, query, hash: current.hash };
    navigating.add( router );
    tracker.ownTarget = router.resolve( location ).fullPath;
    try {
        await ( write.push ? router.push( location ) : router.replace( location ) );
    } catch {
        // A guard that throws leaves the list as the member set it; only the address bar lags
    } finally {
        navigating.delete( router );
        tracker.ownTarget = null;
    }
}

/**
 * Merges `patch` into the current route's query. Every patch asked for before the write starts lands as one
 * navigation, pushed when any of them asked for a push. Resolves once it has landed (or was dropped).
 */
export function patchRouteQuery( router: Router, patch: QueryPatch, history: QueryHistoryMode = 'replace' ): Promise< void > {
    trackNavigations( router );
    const path: string = router.currentRoute.value.path;
    let write: ScheduledWrite | undefined = scheduledWrites.get( router );
    if ( !write || write.path !== path ) {
        const created: ScheduledWrite = { path, patch: {}, push: false };
        write = created;
        scheduledWrites.set( router, created );

        const previous: Promise< void > = writeChains.get( router ) ?? Promise.resolve();
        const done: Promise< void > = Promise.all( [ previous, nextTick() ] ).then( () => flushWrite( router, created ) );
        writeChains.set( router, done );
    }

    Object.assign( write.patch, patch );
    write.push ||= history === 'push';
    return writeChains.get( router )!;
}

/** True while a query write of ours is waiting to start or still navigating. */
export function isRouteQueryWritePending( router: Router ): boolean {
    return scheduledWrites.has( router ) || navigating.has( router );
}

/*********************************************************************/
//                          ROUTE OWNERSHIP                          //
/*********************************************************************/

function leafRecord( route: RouteLocationNormalizedLoaded ): RouteRecordNormalized | undefined {
    const record: RouteRecordNormalized | undefined = route.matched[ route.matched.length - 1 ];
    return record?.aliasOf ?? record;
}

/**
 * Whether the router is still on the route a view was set up on. A view reads and writes the query only there: a
 * route change on the way out (or to a flyout's own path) must not clear its state or write its keys onto another
 * page.
 *
 * `routeNames` names records that are one page with their own addresses, whose component vue-router keeps between
 * them: the sector list at /sectors and at its flyout's /sectors/:sector. Set up on any of them, the view owns all of
 * them, so entering at /sectors/xlk and closing the flyout still writes the sort. Set up anywhere else (the same
 * component inside a product page), it owns only that record. Names, never the matched component:
 * CachedComponentWrapper backs about 15 unrelated routes.
 */
export function createRouteOwnership( router: Router, routeNames: readonly RouteRecordNameGeneric[] = [] ): () => boolean {
    trackNavigations( router );
    const owner: RouteRecordNormalized | undefined = leafRecord( router.currentRoute.value );
    const sharedNames: ReadonlySet< RouteRecordNameGeneric > = new Set( routeNames );
    const isShared = ( record: RouteRecordNormalized | undefined ): boolean =>
        record?.name !== undefined && sharedNames.has( record.name );
    const ownsShared: boolean = isShared( owner );
    return (): boolean => {
        if ( !owner ) return false;
        const current: RouteRecordNormalized | undefined = leafRecord( router.currentRoute.value );
        return current === owner || ( ownsShared && isShared( current ) );
    };
}

/**
 * The app's router, or null outside one (a component spec without a router). Read from the app's $router rather than
 * injected, so it needs nothing from 'vue-router' at runtime and works under a spec's partial vi.mock( 'vue-router' ).
 */
export function injectRouter(): Router | null {
    const router: unknown = getCurrentInstance()?.appContext.config.globalProperties.$router;
    return ( router as Router | undefined ) ?? null;
}

/*********************************************************************/
//                         ONE QUERY-KEY VALUE                       //
/*********************************************************************/

/**
 * A value kept in one query key: it starts from the URL, follows the URL on Back/Forward, and writes the URL when set.
 * The value changes at once, before the navigation lands, so a control bound to it never lags. Outside a router it is
 * plain local state.
 */
export function useRouteQueryState< T >( key: string, codec: RouteQueryCodec< T >, options: RouteQueryStateOptions = {} ): WritableComputedRef< T > {
    const router: Router | null = injectRouter();
    const state = shallowRef< T >( codec.parse( router ? firstQueryValue( router.currentRoute.value.query[ key ] ) : undefined ) );

    const isOwnRoute: () => boolean = router ? createRouteOwnership( router, options.routeNames ) : () => false;
    if ( router ) {
        watch( () => router.currentRoute.value.query[ key ], ( raw ) => {
            // Our own write landing (or a newer one queued) is not news; anything else is Back, Forward or a link
            if ( !isOwnRoute() || isRouteQueryWritePending( router ) ) return;

            const next: T = codec.parse( firstQueryValue( raw ) );
            if ( codec.serialize( next ) !== codec.serialize( state.value ) ) {
                state.value = next;
            }
        } );
    }

    return computed< T >( {
        get: () => state.value,
        set: ( value: T ) => {
            state.value = value;
            if ( router && isOwnRoute() ) {
                void patchRouteQuery( router, { [ key ]: codec.serialize( value ) }, options.history ?? 'replace' );
            }
        }
    } );
}
